/**
 * English message table. The keys-not-strings convention
 * (ADR-0041 §"Internationalization") means every UI string flows through
 * `t("...")` — replace this object with a locale-aware provider in v1.5.
 *
 * Naming: dotted hierarchy `<surface>.<element>` — e.g. `home.welcome`,
 * `catalog.biomarker.add`. Add keys here as the UI grows; do not inline
 * strings in components.
 */
export const messages = {
  "app.title": "Smart Reports",
  "app.sign_out": "Sign out",
  "signed_out.title": "You have signed out",
  "signed_out.body": "Sign in again to use Smart Reports.",
  "signed_out.sign_in": "Sign in",
  "login.redirecting": "Redirecting to sign in...",
  "session.ending_title": "Your session is about to end",
  "session.ending_body":
    "We could not extend your session, so it ends in about {minutes} min. Sign in again " +
    "now to keep working. Anything not yet saved on this page will be lost.",
  "session.sign_in_again": "Sign in again",
  "error.title": "Something went wrong",
  "error.subtitle": "The page hit an unexpected error. Reloading usually clears it.",
  "error.detail_title": "Error detail",
  "error.reload": "Reload",
  "home.welcome": "Smart Reports",
  "home.view_catalogs": "View catalogs",
  "home.review_audit": "Review audit events",
  "home.open_portal": "Client portal",
  "home.open_reports": "Lab reports",
  "home.no_access_title": "No Smart Reports access yet",
  "home.no_access_body":
    "You are signed in, but your account does not have access to anything here yet. " +
    "Ask your lab administrator to give you access.",
  "home.reports_off_title": "The Reports page is not switched on here",
  "home.reports_off_body":
    "Your account can read your lab\u2019s reports, but the Reports page is not switched on for this portal yet. Ask your Medicus contact to switch it on. This is not a problem with your permissions.",
  "home.permissions_unavailable_title": "Could not check your access",
  "home.permissions_unavailable_body":
    "We could not reach the permissions service, so we cannot tell what you have access to. " +
    "Sections are hidden until we can. Reload to try again — if it persists, this is an " +
    "outage rather than a permissions problem.",
  "catalogs.title": "Catalogs",
  "catalogs.loading": "Loading catalogs",
  "catalogs.empty": "No catalogs are visible to your account.",
  "catalogs.error_title": "Could not load catalogs",
  "catalogs.error_generic": "An unexpected error occurred.",
  "catalogs.col_tier": "Tier",
  "catalogs.col_org": "Organization",
  "catalogs.col_branch": "Branch",
  "catalogs.col_biomarkers": "Biomarkers",
  "catalog_detail.loading": "Loading catalog",
  "catalog_detail.error_title": "Could not load catalog",
  "catalog_detail.filter_placeholder": "Filter by symbol or name…",
  "catalog_detail.count_prefix": "Showing",
  "catalog_detail.no_matches": "No biomarkers match the filter.",
  "catalog_detail.col_symbol": "Symbol",
  "catalog_detail.col_display": "Display name",
  "catalog_detail.col_abbrev": "Abbreviation",
  "catalog_detail.col_panel": "Panel",
  "catalog_detail.col_type": "Type",
  "catalog_detail.grouper": "Grouper",
  "biomarker.loading": "Loading biomarker",
  "biomarker.error_title": "Could not load biomarker",
  "biomarker.not_found_title": "Biomarker not found",
  "biomarker.not_found_body": "No biomarker in this catalog with symbol",
  "biomarker.calculated": "Calculated",
  "biomarker.classification": "Classification",
  "biomarker.variable_name": "Variable name",
  "biomarker.sample_type": "Sample type",
  "biomarker.coverage_type": "Coverage type",
  "biomarker.categories": "Categories",
  "biomarker.calculated_from": "Calculated from",
  "biomarker.members": "Members",
  "biomarker.codes": "Codes",
  "biomarker.secondary_loinc": "Secondary LOINC",
  "biomarker.units": "Units",
  "biomarker.unit_code": "Unit",
  "biomarker.unit_canonical": "Canonical",
  "biomarker.canonical": "canonical",
  "biomarker.content": "Clinical content",
  "biomarker.what_why": "What / Why",
  "biomarker.how": "How (test preparation)",
  "catalog_detail.new_biomarker": "+ New biomarker",
  "catalog_detail.bulk_upload": "Bulk upload",
  "spreadsheet.title": "Bulk upload",
  "spreadsheet.subtitle":
    "Upload a clinical-content spreadsheet (Biomarkers list / Biomarker LRs / Biomarker units) for a dry-run preview — nothing is written.",
  "spreadsheet.file_label": "Spreadsheet",
  "spreadsheet.file_placeholder": "Pick an .xlsx / .csv file",
  "spreadsheet.preview": "Preview",
  "spreadsheet.error_title": "Could not parse the spreadsheet",
  "spreadsheet.result_title": "Preview",
  "spreadsheet.imported_count": "Parsed biomarkers:",
  "spreadsheet.skipped_count": "skipped:",
  "spreadsheet.imported": "Parsed biomarkers",
  "spreadsheet.parse_errors": "Row parse errors",
  "spreadsheet.col_sheet": "Sheet",
  "spreadsheet.col_row": "Row",
  "spreadsheet.col_message": "Message",
  "spreadsheet.validation_errors": "Catalog validation errors",
  "new_biomarker.title": "New biomarker",
  "new_biomarker.required_hint": "Required per schema:",
  "new_biomarker.symbol_desc": "Canonical symbol (uppercase, ≤20 chars) — the stable key.",
  "new_biomarker.categories_desc": "Press Enter to add each category (e.g. low, normal, high).",
  "new_biomarker.canonical_unit": "Canonical unit",
  "new_biomarker.unit_desc": "The unit results are stored in (e.g. mg/dL).",
  "new_biomarker.preview": "Biomarker JSON (what would be submitted)",
  "new_biomarker.copy": "Copy JSON",
  "new_biomarker.copied": "Copied!",
  "new_biomarker.copied_toast": "Biomarker JSON copied to clipboard.",
  "new_biomarker.copy_blocked": "Fix the highlighted fields before copying.",
  "new_biomarker.validate": "Validate",
  "new_biomarker.validate_ok_title": "Valid",
  "new_biomarker.validate_fail_title": "Validation issues",
  "new_biomarker.validate_ok": "No catalog-level issues — this biomarker is ready to submit.",
  "new_biomarker.submit": "Submit as MR",
  "new_biomarker.mr_opened": "Merge request opened",
  "new_biomarker.submit_disabled":
    "MR submission isn't enabled on the server yet (needs SR_MR_SUBMISSION_ENABLED + a GitLab token, ADR-0040 Q1). The draft is valid — copy the JSON into a manual MR meanwhile.",
  "new_biomarker.gate_title": "Server-gated submission",
  "new_biomarker.gate_body":
    "Submit opens a GitLab MR adding this biomarker to the tenant catalog (the live catalog changes only when the MR merges — PR-as-the-gate). It succeeds once the backend has SR_MR_SUBMISSION_ENABLED + a token provisioned; until then Submit returns a clear 'not enabled' message and you can Copy the JSON into a manual MR.",
  "new_biomarker.err_required": "Required.",
  "new_biomarker.err_name_len": "Symbol must be ≤20 characters.",
  "new_biomarker.err_display_len": "Display name must be ≤75 characters.",
  "new_biomarker.err_abbrev_len": "Abbreviation must be ≤20 characters.",
  "new_biomarker.err_categories": "Add at least one category.",
  "new_biomarker.ranges_title": "Reference ranges",
  "new_biomarker.ranges_need_categories":
    "Add categories above to set their numeric [min, max) ranges.",
  "new_biomarker.ranges_desc":
    "Half-open [min, max) bounds per category, in the canonical unit. Leave a category blank to omit it; fill all to define the full bucket.",
  "new_biomarker.range_min": "Min",
  "new_biomarker.range_max": "Max (exclusive)",
  "new_biomarker.add_bucket": "+ Add demographic bucket",
  "new_biomarker.bucket_default": "Default (all patients)",
  "new_biomarker.bucket_n": "Bucket",
  "new_biomarker.remove": "Remove",
  "new_biomarker.filter_sex": "Sex",
  "new_biomarker.filter_age_min": "Age min",
  "new_biomarker.filter_age_max": "Age max",
  "new_biomarker.filter_pregnant": "Pregnant",
  "new_biomarker.any": "Any",

  // --- Rules/content authoring + simulation (phase2-admin-ui slice) ---
  "authoring.rules_title": "Rules",
  "authoring.content_title": "Content",
  "authoring.simulate_title": "Simulator",
  "authoring.region": "Region",
  "authoring.lang": "Language",
  "authoring.org_filter": "Organization (optional)",
  "authoring.org_placeholder": "org id",
  "authoring.org_pinned_label": "Organization",
  "authoring.search": "Filter",
  "authoring.search_placeholder": "insight id, biomarker, or type\u2026",
  "authoring.content_search_placeholder": "insight id\u2026",
  "authoring.rules_loading": "Loading rules",
  "authoring.content_loading": "Loading content",
  "authoring.rules_empty": "No rules match.",
  "authoring.content_empty": "No content matches.",
  "authoring.rules_count_suffix": "rules shown",
  "authoring.content_count_suffix": "insights shown",
  "authoring.error_title": "Request failed",
  "authoring.error_generic": "Something went wrong \u2014 try again.",
  "authoring.col_insight": "Insight",
  "authoring.col_type": "Type",
  "authoring.col_biomarkers": "Biomarkers",
  "authoring.col_conditions": "Conditions",
  "authoring.col_priority": "Priority",
  "authoring.col_regions": "Regions",
  "authoring.col_languages": "Languages",
  "authoring.col_lifecycle": "Lifecycle",
  "authoring.col_severity": "Severity",
  "authoring.col_citations": "Citations",
  "authoring.badge_override": "org override",
  "authoring.rule_not_found": "Rule not found",
  "authoring.rule_not_found_body":
    "No rule with this insight id in the selected region's effective set.",
  "authoring.draft_label": "Rule draft (JSON)",
  "authoring.draft_desc":
    "Edit the rule as JSON. You may write biomarkers by symbol, display name, or synonym (\u201cglucose\u201d) \u2014 Validate rewrites them to canonical symbols and Submit commits that canonical form. Validate runs the backend cross-checks without writing anything; Submit opens a review MR (org-namespaced rules only \u2014 base rules are edited by saving a copy under your org's namespace).",
  "authoring.draft_invalid_json": "Draft is not valid JSON.",
  "authoring.normalized_prefix": "Normalized to canonical symbols:",
  "authoring.apply_normalized": "Apply to draft",
  "authoring.sim_resolved_prefix": "Symbols normalized before evaluation:",
  "authoring.validate": "Validate",
  "authoring.validate_ok": "Draft is valid",
  "authoring.validate_failed": "Draft failed validation",
  "authoring.submit_org": "Submit organization",
  "authoring.submit_mr": "Submit as MR",
  "authoring.mr_opened": "Merge request opened",
  "authoring.open_simulator": "Open the simulator \u2192",
  "authoring.check_conflicts": "Check conflicts",
  "authoring.conflicts_none": "No overlapping rules found on this draft's biomarker(s).",
  "authoring.conflicts_title": "Overlapping rules",
  "authoring.conflicts_advisory":
    "Advisory: every matching rule fires, so a range that overlaps another rule's produces two readings for one biomarker. This is not a gate \u2014 review, don't assume.",
  "authoring.conflict_kind_contradiction_risk": "Contradiction risk",
  "authoring.conflict_kind_duplicate": "Duplicate",
  "authoring.conflict_kind_same_insight_overlap": "Same insight (accumulates)",
  "authoring.conflict_kind_co_fire": "Co-fires",
  "authoring.conflict_possible": "possible",
  "authoring.conflict_shared": "shares",
  "authoring.citation_editor_title": "Citation editor",
  "authoring.citation_editor_intro":
    "Lint a citation before it goes to the base corpus. The citation corpus is global and committed on the governed git path — this validates shape, id-collision and the DOI/PMID/URL, it does not write.",
  "authoring.citation_id": "Citation id",
  "authoring.citation_type": "Type / evidence level",
  "authoring.citation_title": "Title",
  "authoring.citation_year": "Year",
  "authoring.citation_publisher": "Publisher",
  "authoring.citation_url": "URL / DOI / PMID",
  "authoring.citation_recency": "Recency status",
  "authoring.citation_regions": "Region applicability (comma-separated)",
  "authoring.citation_date_accessed": "Date accessed (YYYY-MM-DD)",
  "authoring.citation_validate": "Validate citation",
  "authoring.citation_valid": "Citation shape is valid",
  "authoring.citation_invalid": "Citation has errors",
  "authoring.citation_id_exists": "This id already exists in the corpus",
  "authoring.citation_identifier": "Identifier",
  "authoring.citation_preview": "Preview",
  "authoring.org_namespace_hint": "Submittable rules use the org namespace",
  "authoring.content_pair_missing": "No content for this (region, language) pair",
  "authoring.citations_label": "Citations",
  "authoring.citations_edit_label": "Citations (at least one required)",
  "authoring.citations_required": "Add at least one citation before submitting (ADR-0006).",
  "authoring.citations_remove": "Remove citation",
  "authoring.citations_add_placeholder": "Search the citation corpus by id or title\u2026",
  "authoring.edit_as_override": "Edit as org override\u2026",
  "authoring.override_draft_label": "Org-override insight file (JSON)",
  "authoring.override_draft_desc":
    "Written to content/clients/{org}/ via a review MR. Your (region, language) entries replace base; anything you omit falls through to base (ADR-0013).",
  "authoring.simulate_intro":
    "Runs a synthetic test case against the real rule engine (values in canonical units). Paste a draft rule to see a baseline-vs-draft diff before opening an MR.",
  "authoring.sim_case_title": "Test case",
  "authoring.sim_biomarker": "Biomarker (symbol or synonym)",
  "authoring.sim_value": "Value",
  "authoring.sim_unit": "Unit",
  "authoring.sim_add_row": "+ Add biomarker",
  "authoring.sim_remove_row": "Remove row",
  "authoring.sim_gender": "Gender",
  "authoring.sim_age": "Age",
  "authoring.sim_conditions": "Conditions (comma-separated)",
  "authoring.sim_medications": "Medications (comma-separated)",
  "authoring.sim_draft_label": "Draft rule (JSON, optional)",
  "authoring.sim_draft_desc":
    "When present, the simulation runs twice \u2014 baseline and with the draft injected \u2014 and shows the fired-insight diff. Synonyms are canonicalized before the engine runs.",
  "authoring.sim_run": "Run simulation",
  "authoring.sim_elapsed": "Evaluation time",
  "authoring.sim_batch_title": "Batch (multiple patients)",
  "authoring.sim_batch_help":
    "A JSON array of patient objects (same shape as a single patient). Runs each against the region / org / draft above and reports the per-patient diff and latency.",
  "authoring.sim_batch_run": "Run batch",
  "authoring.sim_batch_invalid": "Batch input must be a JSON array of patient objects.",
  "authoring.sim_batch_col_patient": "Patient",
  "authoring.sim_batch_col_fired": "Fired (base / draft)",
  "authoring.sim_batch_col_diff": "Diff (draft)",
  "authoring.sim_batch_col_ms": "ms",
  "authoring.sim_batch_avg": "Average evaluation time",
  "authoring.sim_batch_row_error": "error",
  "authoring.sim_unknown_title": "Not in the catalog (excluded from the run)",
  "authoring.sim_bad_unit_title": "Unit not recognised for this biomarker (excluded from the run)",
  "authoring.sim_bad_unit_declared": "declares",
  "authoring.sim_baseline": "Baseline",
  "authoring.sim_with_draft": "With draft",
  "authoring.sim_fired_suffix": "rules fired",
  "authoring.sim_none": "No insights fired.",
  "authoring.sim_draft_fired": "draft rule fired",
  "authoring.sim_draft_not_fired": "draft rule did not fire",
  "authoring.sim_reading": "Reading",
  "authoring.search_title": "Search rules & content",
  "authoring.search_intro":
    "Find rules by the biomarkers they reference, by patient-profile predicates (conditions, smoker status, \u2026), or by searching the authored content text. Criteria combine (AND).",
  "authoring.search_biomarkers": "Biomarkers (comma-separated symbols)",
  "authoring.search_match": "Biomarker match",
  "authoring.search_match_all": "all of them",
  "authoring.search_match_any": "any of them",
  "authoring.search_profile": "Profile / condition terms (comma-separated)",
  "authoring.search_text": "Content text",
  "authoring.search_text_placeholder": "e.g. insulin resistance (min 2 characters)",
  "authoring.search_run": "Search",
  "authoring.matched_synonym": "matched synonym:",
  "authoring.matched_fuzzy": "did you mean:",
  "authoring.suggest_loading": "Searching\u2026",
  "authoring.suggest_empty": "No matching biomarker \u2014 free text still works.",
  "authoring.kind_profile": "patient profile",
  "authoring.cb_attribute": "Attribute",
  "authoring.cb_operator": "Operator",
  "authoring.cb_value": "Value",
  "authoring.cb_value_min": "Min",
  "authoring.cb_value_max": "Max",
  "authoring.cb_add": "+ Add condition",
  "authoring.cb_remove": "Remove condition",
  "authoring.cb_empty": "No conditions \u2014 this rule fires for every patient.",
  "authoring.cb_numeric_hint": "Numeric biomarker \u2014 enter a value in its canonical unit.",
  "authoring.cb_pick_attribute": "Pick an attribute first.",
  "authoring.cb_unknown_attribute":
    "Unknown attribute \u2014 not a catalog biomarker or a profile attribute.",
  "authoring.tab_form": "Conditions (form)",
  "authoring.tab_json": "Raw JSON",
  "authoring.cb_sentinel_group": "Sentinels",
  "authoring.cb_values_group": "Values",
  "authoring.search_fuzzy": "Synonyms & near-matching",
  "authoring.search_resolved_prefix": "Interpreted:",
  "authoring.search_need_criteria": "Enter at least one criterion.",
  "authoring.search_rules_heading": "Matching rules",
  "authoring.search_content_heading": "Content matches",
  "authoring.search_col_field": "Field",
  "authoring.search_col_snippet": "Snippet",
  "authoring.search_truncated": "Content hits were capped \u2014 narrow the text query.",
  "home.search_authoring": "Search rules & content",
  "portal.title": "Client Portal",
  "portal.subtitle": "Managing organization",
  "portal.organization": "Organization",
  "portal.users": "Users & privileges",
  "portal.users_loading": "Loading users",
  "portal.users_empty": "No users in this organization yet.",
  "portal.users_unavailable": "Couldn\u2019t load users",
  "portal.col_user": "User",
  "portal.col_email": "Email",
  "portal.col_status": "Status",
  "portal.col_roles": "Roles",
  "portal.col_actions": "Actions",
  "portal.enabled": "enabled",
  "portal.disabled": "disabled",
  "portal.action_grant": "Grant",
  "portal.action_revoke": "Revoke",
  "portal.action_disable": "Disable",
  "portal.action_enable": "Enable",
  "portal.action_failed": "That didn\u2019t work",
  "portal.grant_placeholder": "Add a role\u2026",
  "portal.group_phi_suffix": "needs approval",
  "portal.roles_none": "no roles",
  "portal.roles_unknown": "not shown",
  "portal.roles_unknown_detail":
    "Medicus IAM didn\u2019t return this user\u2019s roles on the list. That is not the same as \u201cno roles\u201d \u2014 don\u2019t assume this user has none.",
  "portal.phi_request_title": "This role needs approval",
  "portal.phi_request_body":
    "This role can see patient data, so it can\u2019t be granted directly \u2014 not even by an admin. Request it here: a second person has to approve, and the access expires on its own.",
  "portal.phi_request_justification": "Why does this person need it?",
  "portal.phi_request_justification_hint":
    "The approver reads this. Say what they\u2019ll be doing with it.",
  "portal.phi_request_submit": "Send request",
  "portal.invite_user": "Invite user",
  "portal.invite_title": "Invite a new user",
  "portal.invite_body":
    "They’ll be added to your organization and emailed a link to set their password. You choose their starting role here; you can adjust what they can do afterwards with roles.",
  "portal.invite_username": "Username",
  "portal.invite_email": "Email",
  "portal.invite_email_hint": "Where the set-password link is sent.",
  "portal.invite_role": "Starting role",
  "portal.invite_role_hint":
    "Set once, at creation. Some roles may be refused if your account can’t assign them.",
  "portal.invite_role_placeholder": "Choose a role",
  "portal.invite_first_name": "First name (optional)",
  "portal.invite_last_name": "Last name (optional)",
  "portal.invite_submit": "Send invite",
  "portal.cancel": "Cancel",
  "portal.disable_confirm_title": "Disable this user?",
  "portal.disable_confirm_body":
    "They will not be able to sign in until a lab admin enables them again. Their roles are kept.",
  "portal.disable_confirm_submit": "Disable user",
  "portal.phi_request_min_hint": "At least 10 characters.",
  "portal.invite_role_not_granted_title": "Invite sent, role not added",
  "portal.scope_note":
    "Everything here is scoped to your organization. You cannot see or change another tenant\u2019s data \u2014 enforced server-side, not by this UI.",
  "portal.approvals": "Access approvals",
  "portal.approvals_subtitle":
    "Roles that can see patient data can\u2019t be granted directly \u2014 not even by an admin. They\u2019re requested here, and someone other than the requester has to approve. Access is time-boxed and lapses on its own.",
  "portal.approvals_loading": "Loading requests",
  "portal.approvals_empty": "Nothing waiting for approval.",
  "portal.approvals_unavailable": "Couldn\u2019t load the approval queue",
  "portal.approval_grants_phi": "can see patient data",
  "portal.approval_requested_by": "Requested by",
  "portal.approval_duration_default": "90 days (default)",
  "portal.approval_days": "days",
  "portal.approval_approve": "Approve",
  "portal.approval_deny": "Deny",
  "portal.approval_no_justification": "No reason given.",
  "portal.approval_refused": "That decision was refused",
  "portal.approval_you": "you",
  "portal.approval_own_request":
    "You can\u2019t approve your own request. Another lab admin has to approve it.",
  "portal.audit": "Audit log",
  "portal.audit_subtitle":
    "Clinical alerts and system events recorded for your organization, newest first.",
  "portal.audit_loading": "Loading audit events",
  "portal.audit_empty": "No events match this filter.",
  "portal.audit_unavailable": "The audit log isn\u2019t available",
  "portal.audit_preset": "Show",
  "portal.audit_preset_critical": "Critical clinical alerts",
  "portal.audit_preset_all_triggers": "All clinical alerts",
  "portal.audit_preset_everything": "Everything",
  "portal.audit_window": "Time window",
  "portal.audit_window_24h": "Last 24 hours",
  "portal.audit_window_7d": "Last 7 days",
  "portal.audit_window_30d": "Last 30 days",
  "portal.audit_refresh": "Refresh",
  "portal.audit_col_time": "Time",
  "portal.audit_col_event": "Event",
  "portal.audit_col_severity": "Severity",
  "portal.audit_col_patient": "Patient",
  "portal.audit_col_service": "Service",
  "portal.audit_phi_title": "You may not be seeing every alert",
  "portal.audit_phi_note":
    "Alerts about a patient are only visible to reviewers granted PHI audit access. If you expect alerts and see none, your account may lack that privilege rather than the alerts being absent.",
  "portal.audit_count_suffix": "events",
  "portal.reports": "Lab reports",
  "portal.reports_subtitle":
    "Smart Reports generated from your lab\u2019s results, newest first, with where each one stands in review. Open one to see what it carries.",
  "portal.reports_loading": "Loading reports",
  "portal.reports_empty": "No reports yet for your lab.",
  "portal.reports_empty_filtered": "No reports in this review state.",
  "portal.reports_unavailable":
    "The reports list isn\u2019t available right now. Try again shortly.",
  "portal.reports_forbidden": "Your account cannot open lab reports.",
  "portal.reports_filter": "Review state",
  "portal.reports_filter_all": "All reports",
  "portal.reports_col_report": "Report ID",
  "portal.reports_col_generated": "Generated",
  "portal.reports_col_severity": "Highest severity",
  "portal.reports_col_review": "Review state",
  "portal.reports_col_status": "Lab result status",
  "portal.reports_col_patient": "Patient",
  "portal.reports_col_birth_date": "Date of birth",
  "portal.reports_col_sex": "Sex",
  "portal.reports_col_order": "Lab order number",
  "portal.reports_col_supersedes": "Replaces",
  "portal.reports_showing": "Showing",
  "portal.reports_of": "of",
  "portal.reports_count_suffix": "reports",
  "portal.reports_prev": "Previous",
  "portal.reports_next": "Next",
  "portal.review_state_none": "Not in review",
  "portal.review_state_pending_review": "Pending review",
  "portal.review_state_approved": "Approved",
  "portal.review_state_released": "Released",
  "portal.review_state_withdrawn": "Withdrawn",
  "portal.report_review": "Report review",
  "portal.report_branded_preview": "See the patient page in your lab's branding",
  "portal.report_branded_preview_title": "Patient page preview",
  "portal.report_branded_preview_note":
    "A sample report in your lab's saved branding, as your patients see it. It is not this patient's report.",
  "portal.report_branded_preview_failed": "The preview could not be loaded. Try again shortly.",
  "portal.report_branded_preview_back": "Back to the report",
  "portal.report_review_loading": "Loading report",
  "portal.report_review_not_found": "Report not found.",
  "portal.report_review_unavailable":
    "This report isn\u2019t available right now. Try again shortly.",
  "portal.report_review_note":
    "A review of what this report carries, not the patient\u2019s view of it. Every insight is shown: the content your lab has chosen to send patients is not applied here yet.",
  "portal.report_review_note_span":
    "This is the report as the patient will receive it, at your lab\u2019s content setting:",
  "portal.report_review_outside_span_one":
    "insight in this report is not sent at this setting and is not shown here.",
  "portal.report_review_outside_span_many":
    "insights in this report are not sent at this setting and are not shown here.",
  "portal.report_review_span_fail_closed":
    "Your lab\u2019s content setting could not be read just now, so this shows the default setting, M. It is read again when the report is sent.",
  "portal.report_review_span_flag_off":
    "Lab content settings are not switched on yet, so every lab\u2019s patients receive the default setting, M.",
  "portal.report_review_generated": "Generated",
  "portal.report_review_results": "Results",
  "portal.report_review_results_empty": "No interpreted results in this report.",
  "portal.report_review_passthrough": "Results not interpreted",
  "portal.report_review_passthrough_note":
    "Sent as the lab reported them, with no band or severity, for the reason shown.",
  "portal.report_review_insights": "Insights",
  "portal.report_review_insights_empty": "No insights in this report.",
  "portal.report_review_disclosures": "Content missing from this report",
  "portal.report_review_notices": "Notes on this report",
  "portal.report_review_held_back_title": "Not sent to the patient",
  "portal.report_review_held_back_count_one":
    "insight your lab\u2019s content setting keeps from the patient",
  "portal.report_review_held_back_count_many":
    "insights your lab\u2019s content setting keeps from the patient",
  "portal.report_review_held_back_note":
    "These insights are in the report, but your lab\u2019s content setting does not send them to the patient. They are shown for your information only and are not part of what you approve.",
  "portal.identity_name": "Patient",
  "portal.identity_birth_date": "Date of birth",
  "portal.identity_sex": "Sex",
  "portal.identity_sex_male": "Male",
  "portal.identity_sex_female": "Female",
  "portal.identity_sex_other": "Other",
  "portal.identity_order_number": "Lab order number",
  "portal.identity_patient_number": "Lab patient number",
  "portal.identity_not_available": "Not available",
  "portal.identity_report_id": "Smart Reports ID",
  "portal.identity_lab_report_id": "Lab report ID",
  "portal.report_review_col_result": "Result",
  "portal.report_review_col_value": "Value",
  "portal.report_review_col_band": "Band",
  "portal.report_review_col_severity": "Severity",
  "portal.report_review_col_range": "Normal range",
  "portal.report_review_col_lab_range": "Lab range",
  "portal.report_review_col_reason": "Why not interpreted",
  "portal.report_review_inconclusive": "Inconclusive",
  "portal.report_review_lab_sent_unit": "Converted, the lab sent it in",
  "portal.report_review_banded_as": "Interpreted as",
  "portal.report_review_sr_range": "Smart Reports range, the lab sent none",
  "portal.report_review_severity_none": "None",
  "portal.report_review_related": "Results",
  "portal.report_review_sources": "Sources",
  "portal.report_review_skipped":
    "Part of this report could not be shown. Some of its results or insights are not on this page.",
  "portal.report_approve": "Approve and send",
  "portal.report_withdraw": "Withdraw",
  "portal.report_approve_explained":
    "Releases this report to the patient. Once sent it cannot be changed or withdrawn.",
  "portal.report_withdraw_explained":
    "Stops this report. The patient never receives it, and it cannot be restored.",
  "portal.report_approve_confirm":
    "Send this report to the patient? Once sent it cannot be changed or withdrawn.",
  "portal.report_withdraw_confirm":
    "Withdraw this report? The patient will not receive it, and this cannot be undone.",
  "portal.report_confirm": "Confirm",
  "portal.report_cancel": "Cancel",
  "portal.report_approved_done": "Approved and released.",
  "portal.report_withdrawn_done": "Withdrawn. The patient will not receive this report.",
  "portal.report_act_conflict":
    "This report has changed since you opened it. Refresh the page to see where it stands.",
  "portal.report_act_forbidden": "Only a clinician of this lab can approve or withdraw reports.",
  "portal.report_act_unavailable":
    "That did not go through. Refresh the page to check whether the report changed, then try again.",
  "portal.pin_reissue": "Send a new PIN",
  "portal.pin_reissue_confirm":
    "Send the patient a new PIN? The old PIN stops working at once. If the link is still open, it stays the same and keeps its expiry date. If it has expired or expires within a day, the patient gets a new link that stays open for 30 days.",
  "portal.pin_reissue_done":
    "A new PIN was set and its email to the patient is queued. The old PIN no longer works. Whole days left on the link: {days}.",
  // docs#219: the old link had expired or was in its last day. Wording pending approval.
  "portal.pin_reissue_new_link_done":
    "The patient's link had expired or was about to expire, so we're emailing them a new link with a new PIN. It stays open for {days} days.",
  "portal.pin_reissue_not_emailed":
    "The PIN was replaced, but no email went out, so the patient cannot open the report yet. Press Send a new PIN again.",
  "portal.pin_reissue_conflict": "A new PIN cannot be sent for this report, refresh the page",
  "portal.pin_reissue_unavailable":
    "The new PIN could not be confirmed. The old PIN may no longer work, please try again in a few minutes.",
  "portal.pin_reissue_forbidden": "Only a clinician of this lab can send a new PIN.",
  // The orchestrator's fixed refusal sentences (smart_reports_models.lab_delivery.
  // PIN_REISSUE_REFUSALS), shown only when the server's text is exactly one of these.
  // onboarding-api's test_portal_pin_reissue checks this list against the Python one.
  "portal.pin_reissue_refused_no_delivery":
    "This report has not been sent to the patient, so there is no PIN to replace.",
  "portal.pin_reissue_refused_not_handed_over":
    "This report is still being sent to the patient. Try again in a few minutes.",
  "portal.pin_reissue_refused_delivery_failed":
    "This report never reached the patient's link, so there is no PIN to replace.",
  "portal.pin_reissue_refused_link_expired":
    "The patient's link has expired. A new link is not available from this page yet.",
  "portal.pin_reissue_refused_link_expires_within_a_day":
    "The patient's link expires in less than a day, so a new PIN is not sent. A new link is not available from this page yet.",
  "portal.pin_reissue_refused_report_not_released":
    "This report has not been released to the patient.",
  "portal.pin_reissue_refused_report_superseded":
    "A corrected report replaced this one. Send the new PIN from the corrected report.",
  "portal.pin_reissue_refused_no_patient_email":
    "We cannot email this patient, so a new PIN cannot be sent.",
  "portal.pin_reissue_refused_reissue_in_progress":
    "A new PIN was just requested for this report. Refresh the page.",
  "portal.pin_reissue_refused_reissue_superseded":
    "A newer PIN was already sent for this report. Refresh the page.",
  "portal.pin_reissue_refused_report_summary_refused":
    "The new PIN could not be set. Medicus has been alerted, please do not press again.",
  "portal.pin_reissue_refused_report_summary_unavailable":
    "The new PIN could not be confirmed. The old PIN may no longer work, please try again in a few minutes.",
  "portal.pin_reissue_refused_reissue_unconfirmed":
    "The new PIN is not confirmed yet. Medicus keeps trying and emails the patient a working PIN as soon as one is set, so please do not press again. If no email can go out within 24 hours, your lab is told.",
  "portal.report_attachments": "What the lab sent",
  "portal.report_attachments_loading": "Loading the lab\u2019s attachments",
  "portal.report_attachments_empty": "The lab sent no attachment with this report.",
  "portal.report_attachments_unavailable":
    "The lab\u2019s attachments aren\u2019t available right now. Try again shortly.",
  "portal.report_attachments_original": "The lab\u2019s original report",
  "portal.report_attachments_other": "Attachment",
  "portal.report_attachments_open": "Open",
  "portal.report_attachments_download": "Download",
  "portal.report_attachments_open_failed":
    "This attachment could not be opened. Try again shortly.",
  "portal.insight_type_clinical-reading": "Clinical readings",
  "portal.insight_type_clinical-reading-tip": "Tips",
  "portal.insight_type_screening": "Screenings",
  "portal.insight_type_doctor-visit": "Doctor visits",
  "portal.insight_type_clinical-reading-trend": "Trends",
  "portal.insight_type_health-risk-desc": "Health risks",
  "portal.insight_type_health-risk-tip": "Health risk tips",
  "portal.insight_type_calculator-health-risk-tip": "Risk calculator tips",
  "portal.messages": "Lab messages",
  "portal.messages_subtitle":
    "Every lab message you sent us, what we did with it, and whether a Smart Report was asked for on it. Held messages are one filter: those are results we could not attach because the patient\u2019s identity did not match the record, and each needs a person to check.",
  "portal.messages_loading": "Loading lab messages",
  "portal.messages_empty": "No lab messages in this time window.",
  "portal.messages_unavailable": "The lab message list isn\u2019t available",
  "portal.messages_filter_all": "All messages",
  "portal.messages_filter_processed": "Processed",
  "portal.messages_filter_received": "In progress",
  "portal.messages_col_outcome": "Outcome code",
  "portal.messages_col_report": "Smart Report",
  "portal.messages_col_report_reason": "Why not",
  "portal.messages_col_report_id": "Report ID",
  "portal.messages_col_updated": "Last updated",
  "portal.report_outcome_none": "Not recorded",
  "portal.report_outcome_requested": "Requested",
  "portal.report_outcome_not_requested": "Not requested",
  "portal.report_outcome_generated": "Generated",
  "portal.report_outcome_not_generated": "Not generated",
  "portal.report_reason_generation_off": "Report generation is switched off on this service",
  "portal.report_reason_not_eligible": "Not eligible for a report",
  "portal.report_reason_not_final": "No final report in the message",
  "portal.report_reason_not_processed": "The message was not processed",
  "portal.report_reason_request_failed": "The request for a report did not go through",
  "portal.report_reason_generation_failed": "Report generation failed",
  "portal.report_reason_fhir_read_refused":
    "Not generated yet: we are not yet allowed to read these results",
  "portal.held": "Held lab messages",
  "portal.held_subtitle":
    "Lab results we could not attach because the patient\u2019s identity did not match the record. Nothing was written to the patient. Each one needs a person to check whose result it is.",
  "portal.held_loading": "Loading held messages",
  "portal.held_empty":
    "Nothing is being held. Every message we received was attached or rejected outright.",
  "portal.held_unavailable": "The held-message list isn\u2019t available",
  "portal.held_filter": "Show",
  "portal.held_filter_held": "Held for review",
  "portal.held_filter_failed": "Rejected",
  "portal.held_filter_everything": "Everything",
  "portal.held_window": "Time window",
  "portal.held_window_24h": "Last 24 hours",
  "portal.held_window_7d": "Last 7 days",
  "portal.held_window_30d": "Last 30 days",
  "portal.held_col_message": "Message ID",
  "portal.held_col_status": "Status",
  "portal.held_col_reason": "Reason",
  "portal.held_col_first_seen": "First seen",
  "portal.held_col_updated": "Held since",
  "portal.held_count_suffix": "messages",
  "portal.held_showing": "Showing",
  "portal.held_of": "of",
  "portal.held_prev": "Previous",
  "portal.held_next": "Next",
  "portal.held_what_now": "What to do with these",
  "portal.held_what_now_note":
    "Check the message ID against your LIS to find whose result it is. Once the patient record and the message agree, resend the message unchanged \u2014 it will be attached. Nothing here has been discarded.",
  "admin.audit_title": "Audit log (all tenants)",
  "admin.audit_subtitle":
    "Cross-organization, cross-region audit search for Medicus staff. Leave Organization blank for the fleet view; a tenant token is scoped to its own organization.",
  "admin.audit_filter_org": "Organization",
  "admin.audit_filter_org_placeholder": "All organizations",
  "admin.audit_filter_service": "Service",
  "admin.audit_filter_patient": "Patient ID",
  "admin.audit_filter_event": "Event type (prefix)",
  "admin.audit_col_org": "Organization",
  "admin.audit_apply": "Search",
  "admin.flags_title": "Feature flags",
  "admin.flags_subtitle":
    "The SR_*_ENABLED feature flags each service was deployed with. Read-only — flags are changed by redeploying with a new environment, not from here.",
  "admin.flags_unreachable":
    "Unreachable — this service could not be queried; its flags are not shown.",
  "admin.flags_col_flag": "Flag",
  "admin.flags_col_state": "State",
  "admin.flags_on": "On",
  "admin.flags_off": "Off",
  "admin.flags_none": "No feature flags reported for this service.",
  "admin.flags_unavailable": "Could not load the feature-flag panel.",
  "admin.flags_loading": "Loading feature flags…",
  "portal.misconfigured_title": "Tenant misconfigured",
  "portal.misconfigured_body":
    "Your account is a client administrator, but your sign-in carries no organization. Rather than show you an empty portal \u2014 or, worse, someone else\u2019s \u2014 we\u2019ve stopped here. Ask your Medicus contact to attach an organization to your account.",
  "portal.permissions_unavailable_title": "Could not check your portal access",
  "portal.permissions_unavailable_body":
    "We could not reach the permissions service, so we cannot tell whether your account may open the Client Portal. This is not the same as having no access. Reload to try again, and if it persists this is an outage rather than a permissions problem.",
  "home.view_rules": "Browse rules",
  "home.view_content": "Browse content",
  "home.open_simulator": "Run a simulation",
  "catalog_detail.client_config": "Report presentation",
  "client_config.title": "Report presentation",
  "client_config.intro":
    "Which biomarkers this client's reports show, in what order and under what name. Presentation only: values, severity and which insights fire do not change.",
  "client_config.loading": "Loading report presentation",
  "client_config.error_title": "Could not load the report presentation",
  "client_config.none":
    "No presentation config is served for this organisation, so its reports show every biomarker under its catalog name.",
  "client_config.read_only": "Read only. Changing this needs the catalog:author permission.",
  "client_config.guid_read_only":
    "Editing is by client slug only for now. An organisation id can be viewed here but not submitted (docs#150 item 1).",
  "client_config.biomarkers": "Biomarkers",
  "client_config.panels": "Panels",
  "client_config.col_symbol": "Symbol",
  "client_config.col_panel": "Panel",
  "client_config.col_shown": "Shown",
  "client_config.col_drop_if_empty": "Hide when empty",
  "client_config.col_order": "Order",
  "client_config.col_relabel": "Shown as",
  "client_config.col_remove": "Remove",
  "client_config.yes": "Yes",
  "client_config.no": "No",
  "client_config.unset": "-",
  "client_config.no_biomarker_rules": "No biomarker rules. Every biomarker is shown.",
  "client_config.no_panel_rules": "No panel rules.",
  "client_config.add_biomarker": "Add biomarker",
  "client_config.add_biomarker_placeholder": "Symbol, e.g. HDL",
  "client_config.add_panel": "Add panel",
  "client_config.add_panel_placeholder": "Panel name, e.g. Lipids",
  "client_config.panel_note":
    "Hiding a panel drops its heading only. Hide each biomarker to remove it from the report.",
  "client_config.validate": "Validate and show the change",
  "client_config.submit": "Submit as a merge request",
  "client_config.stale":
    "The draft changed since it was validated. Validate again before submitting.",
  "client_config.valid": "Valid",
  "client_config.invalid": "Not valid",
  "client_config.diff_title": "Change against the served config",
  "client_config.proposed_title": "File the merge request will commit",
  "client_config.no_change": "No change against the served config.",
  "client_config.submitted": "Merge request opened",
  "client_config.submit_failed": "Could not open the merge request",
  "client_config.validate_failed": "Could not validate",
  "client_config.duplicate": "Already in the list",
  // Gate B white-labelling: the Branding and My signature pages and the lab header.
  "home.open_branding_admin": "Lab branding",
  "home.open_branding": "Branding",
  "home.open_signature": "My signature",
  "portal.branding": "Branding",
  "portal.signature": "My signature",
  "branding.header_logo_alt": "Lab logo",
  "branding.portal_title": "Branding",
  "branding.admin_title": "Lab branding",
  "branding.subtitle":
    "How your lab appears to patients on the report page, the PDF and the email. A save goes live for reports released from then on, and reports already sent keep the branding they were sent with.",
  "branding.organization": "Organisation",
  "branding.organization_id": "Or open an organisation by its id",
  "branding.organization_open": "Open",
  "branding.organizations_loading": "Loading organisations",
  "branding.organizations_none": "No organisation matches",
  "branding.organizations_unavailable": "Could not load the organisations you can brand",
  "branding.unbranded": "no branding yet",
  "branding.default_source": "the default",
  "branding.loading": "Loading branding",
  "branding.unavailable": "Could not load this organisation's branding",
  "branding.version": "Version",
  "branding.version_none":
    "Not branded yet. Every field below is empty, so patients see the Smart Reports defaults or what a parent organisation sets.",
  "branding.inherited_from": "Inherited from",
  "branding.inherits_placeholder": "Left empty, the inherited value applies",
  "branding.section_names": "Names",
  "branding.display_name": "Lab name",
  "branding.display_name_ar": "Lab name in Arabic",
  "branding.sender_name": "Email sender name",
  "branding.sender_name_ar": "Email sender name in Arabic",
  "branding.section_look": "Logo and colours",
  "branding.logo": "Logo",
  "branding.primary_color": "Primary colour",
  "branding.primary_color_hint": "Rules and borders. Hex, for example #0a6e8c.",
  "branding.ink_color": "Text colour (optional)",
  "branding.ink_color_hint":
    "Links, buttons and text accents. Left empty, it is worked out from the primary colour.",
  "branding.color_format": "A colour is # and six hex digits, for example #0a6e8c",
  "branding.ink_derived": "Text colour worked out from the primary:",
  "branding.ink_set": "Text colour:",
  "branding.contrast_passes": "Readable, 4.5:1 or more",
  "branding.contrast_fails": "Too light, the save will refuse it",
  "branding.contrast_sample": "Sample text",
  "branding.contrast_on": "on",
  "branding.image_limits": "PNG or JPEG only, at most {cap} and {edge} px on the longer side.",
  "branding.image_none": "None set",
  "branding.image_choose": "Choose an image",
  "branding.image_remove": "Remove",
  "branding.image_size_unknown": "Size not reported",
  "branding.image_type_refused":
    "Only PNG and JPEG images are accepted. SVG, GIF and WebP are refused.",
  "branding.image_too_large": "This image is larger than this slot allows.",
  "branding.image_dimensions":
    "This image is more than 1000 px on its longer side, or has no size.",
  "branding.upload_failed": "The upload did not go through",
  "branding.image_total": "Image bytes on each delivery",
  "branding.image_total_hint":
    "The logo, the stamp and the master signature. Room for the approving doctor's own signature, up to {reserve}, is kept on top.",
  "branding.image_total_unknown":
    "One or more images did not report a size, so the total is lower than what is sent.",
  "branding.section_contact": "Contact details",
  "branding.contact_email": "Contact email",
  "branding.contact_phone": "Phone",
  "branding.contact_phone_hint": "Digits, spaces and + ( ) - . only",
  "branding.contact_address": "Address",
  "branding.contact_address_hint": "Up to four lines",
  "branding.contact_website": "Website",
  "branding.https_hint": "Must start with https://",
  "branding.section_legal": "Terms, privacy and footer",
  "branding.terms_url": "Terms of use link",
  "branding.privacy_url": "Privacy policy link",
  "branding.footer_line": "Footer line (PDF and email)",
  "branding.timezone": "Time zone",
  "branding.timezone_hint": "Dates on the page and the PDF, for example Europe/Vienna",
  "branding.section_signatures": "Stamp and signatures",
  "branding.stamp": "Lab stamp",
  "branding.master_signature_hint":
    "The master signature needs both a name and an image, and a branch never takes its parent's.",
  "branding.master_signature_holder": "Master signature holder",
  "branding.master_signature": "Master signature",
  "branding.section_report_details": "Report details shown",
  "branding.show_approved_by": "Approved by",
  "branding.show_approver_email": "Approver's email",
  "branding.show_prepared_by": "Prepared by",
  "branding.switch_inherit": "Inherit",
  "branding.switch_show": "Show",
  "branding.switch_hide": "Hide",
  "branding.switch_default_on": "Shown by default",
  "branding.prepared_by": "Prepared by text",
  "branding.prepared_by_hint": "For example a department name",
  "branding.section_wording": "Patient email wording",
  "branding.wording_hint":
    "Your own wording is used only in a language where all three emails are written. Every body needs {link} and {pin}, and neither may be in a subject. You may also use {lab} and {days}. Wording goes live when you save.",
  "branding.wording_inherited": "Inherited languages:",
  "branding.wording_add_language": "Add a language",
  "branding.wording_language_hint": "Two or three letter code, for example de or ar",
  "branding.wording_add": "Add",
  "branding.wording_none":
    "No wording of your own. Patients get the approved Smart Reports wording.",
  "branding.wording_report_ready": "Report ready",
  "branding.wording_report_updated": "Report updated",
  "branding.wording_pin_reissued": "New PIN",
  "branding.wording_subject": "Subject",
  "branding.wording_body": "Body",
  "branding.wording_body_hint": "One line per paragraph. Blank lines are left out.",
  "branding.wording_body_one_day": "Body when one day is left",
  "branding.wording_remove_language": "Remove language",
  "branding.error.too_long": "Too long",
  "branding.error.empty": "Empty, or has no letters",
  "branding.error.control_character": "Contains a character that is not allowed",
  "branding.error.not_https": "Must be an https:// link to a named host",
  "branding.error.contrast_too_low": "Too light to read, it must reach 4.5:1 on white",
  "branding.error.image_unknown": "This image was not uploaded for this organisation",
  "branding.error.image_too_large": "This image is too large for this field",
  "branding.error.image_dimensions": "This image is over 1000 px on its longer side",
  "branding.error.master_signature_incomplete":
    "The master signature needs both a name and an image",
  "branding.error.timezone_unknown": "Not a time zone we know, for example Europe/Vienna",
  "branding.error.language_code_invalid": "Not a language code",
  "branding.error.wording_incomplete":
    "Missing: every language needs all three emails and the one-day body",
  "branding.error.placeholder_missing": "Needs both {link} and {pin}",
  "branding.error.placeholder_in_subject": "{link} and {pin} may not be in a subject",
  "branding.error.placeholder_unknown": "Only {lab}, {link}, {pin} and {days} may be used",
  "branding.error.placeholder_malformed":
    "A brace that is not a placeholder, braces cannot be used as text",
  "branding.error.domain_refused": "Not a domain your lab can use",
  "branding.error.format_invalid": "Not in the expected format",
  "branding.save": "Save and publish",
  "branding.save_hint":
    "Live on save for reports released from then on. Every save is recorded with what changed.",
  "branding.saved": "Saved as version",
  "branding.save_refused": "Some values were refused",
  "branding.reload": "Reload the latest version",
  "branding.preview_title": "Preview",
  "branding.preview_page": "Patient page",
  "branding.preview_pdf": "PDF",
  "branding.preview_email": "Email",
  "branding.preview_english": "English",
  "branding.preview_arabic": "Arabic",
  "branding.preview_loading": "Updating the preview",
  "branding.preview_live_hint": "Updates as you edit, from a sample report",
  "branding.preview_failed": "The preview could not be shown",
  "branding.preview_pdf_open": "Open the sample PDF",
  "branding.preview_pdf_hint":
    "A sample report with your current draft, opened in a new tab. Nothing is saved.",
  "branding.preview_email_language": "Language",
  "branding.preview_email_message": "Email",
  "branding.preview_email_one_day": "One day left",
  "branding.preview_email_hint":
    "The email your patients get, rendered by Smart Reports from your draft with a sample link and PIN. Nothing is saved.",
  "branding.preview_email_from": "From:",
  "branding.versions_title": "History",
  "branding.versions_loading": "Loading history",
  "branding.versions_unavailable": "Could not load the history",
  "branding.versions_none": "No versions yet",
  "branding.versions_when": "When",
  "branding.versions_who": "By",
  "branding.versions_changed": "Changed",
  "branding.domains_title": "Your own domains",
  "branding.domains_hint":
    "Patient emails come from your own domain and links open on your own host once each is verified. Until then emails and links use ours, nothing waits. A branch never takes its parent's domains.",
  "branding.sending_domain": "Email sending domain",
  "branding.link_domain": "Patient link host",
  "branding.domain_none": "None, ours is used",
  "branding.domain_pending": "Pending",
  "branding.domain_verified": "Verified",
  "branding.domain_failed": "Failed",
  "branding.domain_reason": "Reason:",
  "branding.domain_set": "Set a domain",
  "branding.domain_change": "Change to",
  "branding.domain_save": "Save domain",
  "branding.domain_remove": "Remove",
  "branding.domain_check": "Check now",
  "branding.dns_purpose": "For",
  "branding.dns_purpose_dkim": "DKIM signing",
  "branding.dns_purpose_mail_from_mx": "MAIL FROM, mail exchanger",
  "branding.dns_purpose_mail_from_spf": "MAIL FROM, SPF",
  "branding.dns_purpose_link_cname": "Patient link host",
  "branding.dns_required": "Needed",
  "branding.dns_required_yes": "Required",
  "branding.dns_required_no": "Optional",
  "branding.dns_found_column": "In DNS",
  "branding.dns_found": "Found",
  "branding.dns_missing": "Not found",
  "branding.dns_not_checked": "Not checked yet",
  "branding.dns_name": "Name",
  "branding.dns_type": "Type",
  "branding.dns_value": "Value",
  "branding.mail_from": "MAIL FROM domain:",
  "branding.cname_instruction": "Add a DNS CNAME record from",
  "branding.cname_to": "to",
  "doctor_signatures.title": "Doctors' signatures",
  "doctor_signatures.hint":
    "Set or remove the signature printed with a doctor's name on the reports they approve. A new one applies to reports approved from now on, and a doctor can also manage their own. A first signature is filed under the organisation open on this page.",
  "doctor_signatures.loading": "Loading your organisation's doctors",
  "doctor_signatures.list_unavailable":
    "Could not list your organisation's doctors. You can still reach a doctor by their IAM user id below.",
  "doctor_signatures.none": "No doctors with a Clinician role in your organisation.",
  "doctor_signatures.doctor": "Doctor",
  "doctor_signatures.signature": "Signature",
  "doctor_signatures.by_id": "A doctor by IAM user id",
  "doctor_signatures.by_id_hint": "For a doctor not listed above, for example in a branch.",
  "doctor_signatures.set": "Upload a signature",
  "doctor_signatures.remove": "Remove signature",
  "doctor_signatures.removed": "No signature on file now.",
  "signature.title": "My signature",
  "signature.subtitle":
    "Your signature is printed with your name on the reports you approve. A new one applies to reports you approve from now on.",
  "signature.loading": "Loading your signature",
  "signature.unavailable": "Could not load your signature",
  "signature.none": "No signature on file. Reports you approve show your name without a signature.",
  "signature.on_file": "Signature on file, version",
  "signature.upload": "Upload a signature",
  "signature.replace": "Replace",
  "signature.remove": "Remove",
  "signature.applies_hint": "Reports already approved keep the signature they were approved with.",
} as const;
